// Global Help / Resources panel (Spec §6.3, Figure 3) — a right-hand slide-over
// with searchable help articles grouped by category, a glossary of the
// specialised concepts, guided-tour launchers, keyboard shortcuts, and a
// support link. Opened from the top-bar Help button, or deep-linked to a
// specific article via a HelpPopover's "Learn more" link (initialTopic).

import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Search, BookOpen, Keyboard, LifeBuoy, Compass } from 'lucide-react'
import { HELP_ARTICLES, GLOSSARY, SHORTCUTS, type HelpArticle } from '../../constants/helpContent'
import { requestTour } from './helpBus'

interface ResourcesPanelProps {
  open: boolean
  onClose: () => void
  /** optionally focus a specific article by key when opened from a ? affordance */
  initialTopic?: string
}

// Fixed display order — categories with no matching articles are skipped.
const CATEGORY_ORDER: HelpArticle['category'][] = [
  'Getting started',
  'Innovations',
  'Research',
  'Network',
  'Organisation',
  'Aggregator',
  'White space',
  'Concepts',
  'Account',
]

interface TourLauncher {
  id: string
  label: string
  description: string
  /** Navigated to before the tour fires — omit for a tour that is
   * project-scoped (no single fixed URL): it replays only while its own
   * page is already mounted and listening via onTourRequest. */
  route?: string
}

// Registered guided tours (Story #5305 §5.8): "Reading your portfolio"
// (Stream 14d) and "Reading your white-space scan" (Stream 11c).
const TOUR_LAUNCHERS: TourLauncher[] = [
  {
    id: 'reading-portfolio',
    label: 'Reading your portfolio',
    description: 'Walks the Atlas, footprints, Synergy vs. Overlap, portfolio shape and the fund-once idea.',
    route: '/agg',
  },
  {
    id: 'reading-whitespace-scan',
    label: 'Reading your white-space scan',
    description: 'Walks the Opportunity Explorer, Combination Matrix, Landscape Map and probing your own directions. Replays while a project’s White Space tab is already open.',
  },
]

export default function ResourcesPanel({ open, onClose, initialTopic }: ResourcesPanelProps) {
  const [q, setQ] = useState('')
  const navigate = useNavigate()
  const [highlightKey, setHighlightKey] = useState<string | null>(null)
  const articleRefs = useRef(new Map<string, HTMLLIElement>())

  const filteredArticles = useMemo(() => {
    const term = q.trim().toLowerCase()
    if (!term) return HELP_ARTICLES
    return HELP_ARTICLES.filter(
      (a) =>
        a.title.toLowerCase().includes(term) ||
        a.body.toLowerCase().includes(term) ||
        a.category.toLowerCase().includes(term),
    )
  }, [q])

  const groupedArticles = useMemo(() => {
    const groups = new Map<HelpArticle['category'], HelpArticle[]>()
    for (const a of filteredArticles) {
      const list = groups.get(a.category) ?? []
      list.push(a)
      groups.set(a.category, list)
    }
    return CATEGORY_ORDER.filter((c) => groups.has(c)).map((c) => ({ category: c, articles: groups.get(c)! }))
  }, [filteredArticles])

  const filteredGlossary = useMemo(() => {
    const term = q.trim().toLowerCase()
    if (!term) return GLOSSARY
    return GLOSSARY.filter((g) => g.term.toLowerCase().includes(term) || g.definition.toLowerCase().includes(term))
  }, [q])

  // Deep-link: on open with an initialTopic, clear any search filter (so the
  // target article is guaranteed to be in the list), scroll to it, and
  // briefly highlight it.
  useEffect(() => {
    if (!open || !initialTopic) return
    setQ('')
    const t = setTimeout(() => {
      const el = articleRefs.current.get(initialTopic)
      el?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      setHighlightKey(initialTopic)
    }, 50)
    return () => clearTimeout(t)
  }, [open, initialTopic])

  useEffect(() => {
    if (!highlightKey) return
    const t = setTimeout(() => setHighlightKey(null), 2200)
    return () => clearTimeout(t)
  }, [highlightKey])

  const launchTour = (tour: TourLauncher) => {
    onClose()
    if (!tour.route) {
      // Project-scoped tour with no single fixed URL — fire immediately;
      // it only does anything while its own page is already mounted.
      requestTour(tour.id)
      return
    }
    navigate(tour.route)
    // Give the destination route a tick to mount before it asks for the
    // tour's target elements.
    setTimeout(() => requestTour(tour.id), 60)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-label="Help and resources" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} aria-hidden />
      <aside className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-iflow-900">
            <LifeBuoy size={20} className="text-iflow-600" aria-hidden /> Help &amp; Resources
          </h2>
          <button type="button" onClick={onClose} aria-label="Close help" className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        <div className="border-b border-slate-100 p-4">
          <div className="relative">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              type="search"
              placeholder="Search help…"
              aria-label="Search help"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-iflow-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-iflow-100"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {TOUR_LAUNCHERS.length > 0 && (
            <section className="mb-6">
              <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                <Compass size={14} aria-hidden /> Guided tours
              </h3>
              <ul className="space-y-2">
                {TOUR_LAUNCHERS.map((tour) => (
                  <li key={tour.id}>
                    <button
                      type="button"
                      onClick={() => launchTour(tour)}
                      data-testid={`tour-launcher-${tour.id}`}
                      className="w-full rounded-lg border border-sky-100 bg-sky-500/5 px-3 py-2 text-left hover:bg-sky-500/10"
                    >
                      <p className="text-sm font-medium text-iflow-900">{tour.label}</p>
                      <p className="text-xs leading-relaxed text-slate-500">{tour.description}</p>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="mb-6">
            <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
              <BookOpen size={14} aria-hidden /> Articles
            </h3>
            {filteredArticles.length === 0 ? (
              <p className="text-sm text-slate-400">No articles match “{q}”.</p>
            ) : (
              groupedArticles.map(({ category, articles }) => (
                <div key={category} className="mb-4 last:mb-0">
                  <p className="mb-1.5 text-[11px] font-semibold text-iflow-500">{category}</p>
                  <ul className="space-y-3">
                    {articles.map((a) => (
                      <li
                        key={a.key}
                        ref={(el) => {
                          if (el) articleRefs.current.set(a.key, el)
                          else articleRefs.current.delete(a.key)
                        }}
                        data-testid={`help-article-${a.key}`}
                        className={`rounded-lg px-1.5 py-1 transition-colors duration-500 ${
                          highlightKey === a.key ? 'bg-amber-100' : 'bg-transparent'
                        }`}
                      >
                        <p className="text-sm font-medium text-iflow-900">{a.title}</p>
                        <p className="text-sm leading-relaxed text-slate-600">{a.body}</p>
                        {a.detail?.map((d, i) => (
                          <p key={i} className="mt-1 text-sm leading-relaxed text-slate-500">{d}</p>
                        ))}
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </section>

          {filteredGlossary.length > 0 && (
            <section className="mb-6">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Glossary</h3>
              <dl className="space-y-2">
                {filteredGlossary.map((g) => (
                  <div key={g.term}>
                    <dt className="text-sm font-medium text-iflow-900">{g.term}</dt>
                    <dd className="text-sm leading-relaxed text-slate-600">{g.definition}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          <section className="mb-6">
            <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
              <Keyboard size={14} aria-hidden /> Keyboard shortcuts
            </h3>
            <ul className="space-y-1.5">
              {SHORTCUTS.map((s) => (
                <li key={s.keys} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{s.action}</span>
                  <kbd className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-xs text-slate-500">{s.keys}</kbd>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <div className="border-t border-slate-200 p-4">
          <a
            href="mailto:support@innovateflow.com.au"
            className="flex items-center justify-center gap-2 rounded-lg bg-iflow-600 px-4 py-2 text-sm font-medium text-white hover:bg-iflow-700"
          >
            <LifeBuoy size={16} aria-hidden /> Contact support
          </a>
        </div>
      </aside>
    </div>
  )
}
